import { Content, Header, LinkButton, Page } from '@backstage/core-components';
import { Grid, TextField } from '@material-ui/core';
/*
 * Copyright 2023 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useEffect, useState } from 'react';

import AdrContent from '../../components/Adr/content';
import { getMarkDownPreview, getYamlContents } from '../../services/yamlValidator.service';
import { EntityTextArea } from './EntityTextArea';
import { EntityValidationOutput } from './EntityValidationOutput';
import { CatalogProcessorResult } from './types';
import { parseEntityYaml } from './utils';

const EXAMPLE_CATALOG_INFO_YAML = `# Put your catalog-info.yaml below and validate it
apiVersion: backstage.io/v1alpha1
kind: Component
metadata:
  name: test
  description: Component description
  links: []
  tags: []
  annotations: {}
spec:
  type: service
  lifecycle: experimental
  owner: owner
`;


function generateSubUrl(path: string) {
  if (!path) return '';

  const parts = path.split('/');

  if (parts.length < 2) return path;

  const secondToLast = parts[parts.length - 2].replace('.md', '');
  const last = parts[parts.length - 1].replace('.md', '');

  if (secondToLast.toLowerCase() === last.toLowerCase()) {
    // remove only the last path segment
    return parts.slice(0, -1).join('/');
  }

  return path;
}


export const EntityValidationPage = (props: {
  defaultYaml?: string;
  defaultLocation?: string;
  from?:"default"|"markDownPreview"
}) => {
  const {
    defaultYaml = EXAMPLE_CATALOG_INFO_YAML,
    defaultLocation = '',
    from
  } = props;
  const [catalogYaml, setCatalogYaml] = useState(defaultYaml);
  const [mdFileContent, setMdFileContent] = useState('');
  const [isLoading,setIsLoading]=useState(false);
  const [yamlFiles, setYamlFiles] = useState<CatalogProcessorResult[]>();
  const [locationUrl, setLocationUrl] = useState('');
  const [error,setError]=useState('')

  const parseYaml = () => {
    const parsedFiles = [
      ...parseEntityYaml(Buffer.from(catalogYaml), {
        type: 'url',
        target: locationUrl ? locationUrl : 'http://localhost',
      }),
    ];
    setYamlFiles(parsedFiles);
  };


  const handleOnChange=async(event:React.ChangeEvent<HTMLTextAreaElement>)=>{
    event.preventDefault();
    setError('')
    setLocationUrl(event.target.value)
    if(!event?.target?.value?.endsWith('yaml')){
        setError('Only yaml files allowed')
        return;
    }
    try{
         const fileContent=await getYamlContents(event.target.value)
         setCatalogYaml(fileContent)
        
    }
    catch(err){
        setError('Could not read the requested file')
        return;
    }
  }

  const handleOnChangeMarkdown=async(data:string,path?:string)=>{
    setError('')
    const value=data?.trim()||path?.trim();

    setLocationUrl(value||path as string)

    if(value && value.trim()){
      let isValidMarkdownUrl = false;
      try {
        const parsedUrl = new URL(value);
        const markdownPath = parsedUrl.searchParams.get('path') ?? parsedUrl.pathname;
        isValidMarkdownUrl =
          parsedUrl.protocol === 'https:' &&
          ['dev.azure.com', 'lhg.ghe.com'].includes(parsedUrl.hostname) &&
          markdownPath.toLowerCase().endsWith('.md');
      } catch {
        isValidMarkdownUrl = false;
      }
      if (!isValidMarkdownUrl) {
        setError('Enter an HTTPS Markdown file URL from an approved host');
        return;
      }

      try{
        setIsLoading(true)
        const fileContent=await getMarkDownPreview(value|| path as string)
        if(fileContent && !fileContent.includes('Cannot read properties of undefined')){
          setMdFileContent(fileContent?.replaceAll('&quot',''))
          const query = new URLSearchParams({ fileLocation: value || path || '' });
          window.history.replaceState(null, '', `mark-down-preview?${query}`);
          setIsLoading(false)
        }
        else{
          setIsLoading(false)
          setError('Could not read the requested file')
        }
           
      }
      catch(err){
          setError('Could not read the requested file')
          setIsLoading(false)
          return;
      }
    }
    
  }
  const sublocation=locationUrl?.split('path=')??''
  console.log(sublocation)

  useEffect(()=>{
    if(from==='markDownPreview' && window?.location?.search){
      const param = new URLSearchParams(window.location.search).get('fileLocation');
      if (param) {
        handleOnChangeMarkdown('', param);
      }
    }
  },[])

  return (
    <Page themeId="tool">
      <Header
        title= { from==='default'?"Entity Validator":"Markdown Preview"}
        subtitle={from==='default'?"Tool to validate catalog-info.yaml files":"Tool to preview your markdown files"}
      />
      <Content>
        <Grid container>
          <Grid item md={from==='default'?8:12} xs={12}>
            <TextField
              fullWidth
              label="File Location"
              margin="normal"
              variant="outlined"
              required
              value={locationUrl}
              placeholder={defaultLocation}
              helperText={from==='default'?"Location where you catalog-info.yaml file is, or will be, located":"Location where your markdown file is located"}
              onChange={from==='default'?handleOnChange:(e)=>handleOnChangeMarkdown(e.target.value)}
            />
            <label style={{color:'red',marginBottom:'-20px'}}>{error}</label>

          </Grid>
          
          <Grid item md={3} xs={12}>
            <Grid container alignItems="center" style={{ height: '100%' }}>
              <Grid item>
              {
                from==='default' &&  <LinkButton size="large" onClick={parseYaml} to="#">
                Validate
              </LinkButton>
              }
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {
          from ==='default' && <Grid container direction="row" style={{ height: '90%' }}>
          <Grid item md={6} xs={12}>
            <EntityTextArea
              onValidate={parseYaml}
              
              onChange={(value: string) => setCatalogYaml(value)}
              catalogYaml={catalogYaml}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <EntityValidationOutput
                  processorResults={yamlFiles}
                  locationUrl={locationUrl}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        }

        {
          from==='markDownPreview' &&  <Grid container direction="row" style={{ height: '90%' }}>
            <AdrContent baseUrl={sublocation[0]?.concat('path=').concat(generateSubUrl(sublocation[1]))} retrievedContent={mdFileContent} isLoading={isLoading} />
          </Grid>
        }
  
      </Content>
    </Page>
  );
};